import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  provingGroundDefaultValues,
  ProvingGroundForm,
  provingGroundFormSchema,
  ProvingGroundFormValues,
} from 'forms/ProvingGroundForm'
import { omitDeep, removeNullProperties } from 'helpers'
import {
  useCreateCommand,
  useDeselectCommand,
  useEditCommand,
  useSearchCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import {
  ProvingGround,
  ProvingGroundInsertType,
  ProvingGroundSetType,
  UserRole,
} from 'model'
import { PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import {
  ReadProvingGroundsQueryVariables,
  useCreateProvingGroundMutation,
  useDeleteProvingGroundMutation,
  useReadProvingGroundsQuery,
  useUpdateProvingGroundMutation,
} from './AdminProvingGroundsView.generated'
import { ProvingGroundList } from './ProvingGroundsList'

const AdminProvingGroundsListView: React.FunctionComponent = () => {
  const { t } = useTranslation()

  const [createProvingGround] = useCreateProvingGroundMutation()
  const [updateProvingGround] = useUpdateProvingGroundMutation()
  const [deleteProvingGround] = useDeleteProvingGroundMutation()
  const [selectedItemId, setSelectedItemId] = React.useState()
  const [filesUploading, setFilesUploading] = React.useState(false)

  const [formType, setFormType] = React.useState(FormType.insert)

  const search = useSearchCommand({
    placeholder: t('model.provingGround.search'),
  })

  const { loading, data, error, refetch } = useReadProvingGroundsQuery({
    variables: { search },
  })
  const items =
    data && data.provingGrounds
      ? data.provingGrounds.filter((x) => !x._deleted)
      : []

  const [selection, selectedItems] = useSelection<ProvingGround>({
    getKey: (item) => item._id,
  })

  const currentProvingGround = localStorage.getItem('currentProvingGround')
  let currentprov: ProvingGround

  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, , hideDialog] = useDialogState()

  if (currentProvingGround) {
    currentprov = JSON.parse(currentProvingGround)
    currentprov = removeNullProperties(currentprov)
    localStorage.removeItem('currentProvingGround')
    setFormType(FormType.update)
    showPanel({ ...currentprov })
  }

  const handleSubmit = React.useCallback(
    async (
      values: ProvingGroundFormValues,
      formikActions: FormikActions<ProvingGroundFormValues>
    ) => {
      formikActions.setSubmitting(true)
      if (formType === FormType.insert) {
        await createProvingGround({
          variables: { input: values as ProvingGroundInsertType },
        })
      } else if (formType === FormType.update) {
        await updateProvingGround({
          variables: {
            _id: selectedItemId,
            update: values as ProvingGroundSetType,
          },
        })
      } else {
        throw new Error('> Error formType not set')
      }
      setFilesUploading(false)
      formikActions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch({
        variables: { search },
      } as ReadProvingGroundsQueryVariables)
      hidePanel()
    },
    [
      createProvingGround,
      updateProvingGround,
      selection,
      selectedItemId,
      setFilesUploading,
      hidePanel,
      refetch,
      formType,
      search,
    ]
  )

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)

      await deleteProvingGround({
        variables: { _ids: ids },
      })
    }

    hideDialog()
  }

  useCreateCommand(
    'provingGround',
    {
      priority: 10,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel(provingGroundDefaultValues)
      },
    },
    [setFormType]
  )
  useEditCommand(
    { name: 'provingGround', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        const pg = selectedItems[0]
        setSelectedItemId(selectedItems[0]._id)
        showPanel(
          removeNullProperties({
            _id: pg._id,
            abbr: pg.abbr,
            countryCode: pg.countryCode,
            inactive: pg.inactive,
            specialNote: pg.specialNote || '',
            title: pg.title,
            hideFerryConnection: pg.hideFerryConnection,
            hideFlight: pg.hideFlight,
            hideNordschleife: pg.hideNordschleife,
            hideHotels: pg.hideHotels,
            skipClearing: pg.skipClearing,
            noteHeaderVehicles: pg.noteHeaderVehicles,
            noteHeaderMaterials: pg.noteHeaderMaterials,
            drivingLessons: pg.drivingLessons,
            racewayHours: pg.racewayHours,
            showNMF: pg.showNMF,
            showSpecialUsage: pg.showSpecialUsage,
            deleteEditor: pg.deleteEditor,
            licensePlateMandatory: pg.licensePlateMandatory,
            participantsMandatory: pg.participantsMandatory,
            vehiclesMandatory: pg.vehiclesMandatory,
            accomodationsMandatory: pg.accomodationsMandatory,
            useUsageTimespansOnly: pg.useUsageTimespansOnly,
            vehicleTransportToDays: pg.vehicleTransportToDays,
            vehicleTransportFromDays: pg.vehicleTransportFromDays,
            outboundTransportSenderId:
              pg.outboundTransportSender && pg.outboundTransportSender._id,
            outboundTransportRecipientId:
              pg.outboundTransportRecipient &&
              pg.outboundTransportRecipient._id,
            returnTransportSenderId:
              pg.returnTransportSender && pg.returnTransportSender._id,
            returnTransportRecipientId:
              pg.returnTransportRecipient && pg.returnTransportRecipient._id,
            noteVehicles: pg.noteVehicles,
            noteMaterials: pg.noteMaterials,
            note1: pg.note1,
            note2: pg.note2,
            note3: pg.note3,
            annotation: pg.annotation,
            clearingIds: pg.clearing,
            editorsIds: pg.editors,
            resourceManagersIds: pg.resourceManagers,
            authorizedEditorsIds: pg.authorizedEditors,
            block1: pg.block1,
            block2: pg.block2,
            block3: pg.block3,
            block4: pg.block4,
            block5: pg.block5,
            block6: pg.block6,
            block7: pg.block7,
            block8: pg.block8,
            block9: pg.block9,
            block10: pg.block10,
            block1Note: pg.block1Note,
            block2Note: pg.block2Note,
            block3Note: pg.block3Note,
            block4Note: pg.block4Note,
            block5Note: pg.block5Note,
            block6Note: pg.block6Note,
            block7Note: pg.block7Note,
            block8Note: pg.block8Note,
            block9Note: pg.block9Note,
            block10Note: pg.block10Note,
            block1Files: pg.block1Files,
            block2Files: pg.block2Files,
            block3Files: pg.block3Files,
            block4Files: pg.block4Files,
            block5Files: pg.block5Files,
            block6Files: pg.block6Files,
            block7Files: pg.block7Files,
            block8Files: pg.block8Files,
            block9Files: pg.block9Files,
            block10Files: pg.block10Files,
            fixedRates: omitDeep(pg.fixedRates, ['__typename']),
          })
        )
      },
    },
    [selectedItems, setFormType]
  )
  // useDeleteCommand(
  //   { name: 'provingGround', count: selectedItems.length },
  //   {
  //     priority: 30,
  //     onClick: showDialog,
  //   },
  //   [selectedItems]
  // )

  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <ProvingGroundList
        items={items}
        selection={selection}
        onItemInvoked={() =>
          selectedItems.length === 1
            ? (() => {
                setFormType(FormType.update)
                const pg = selectedItems[0]
                setSelectedItemId(selectedItems[0]._id)
                showPanel(
                  removeNullProperties({
                    _id: pg._id,
                    abbr: pg.abbr,
                    countryCode: pg.countryCode,
                    inactive: pg.inactive,
                    specialNote: pg.specialNote || '',
                    title: pg.title,
                    hideFerryConnection: pg.hideFerryConnection,
                    hideFlight: pg.hideFlight,
                    hideNordschleife: pg.hideNordschleife,
                    hideHotels: pg.hideHotels,
                    skipClearing: pg.skipClearing,
                    noteHeaderVehicles: pg.noteHeaderVehicles,
                    noteHeaderMaterials: pg.noteHeaderMaterials,
                    drivingLessons: pg.drivingLessons,
                    racewayHours: pg.racewayHours,
                    showNMF: pg.showNMF,
                    showSpecialUsage: pg.showSpecialUsage,
                    deleteEditor: pg.deleteEditor,
                    licensePlateMandatory: pg.licensePlateMandatory,
                    participantsMandatory: pg.participantsMandatory,
                    vehiclesMandatory: pg.vehiclesMandatory,
                    accomodationsMandatory: pg.accomodationsMandatory,
                    useUsageTimespansOnly: pg.useUsageTimespansOnly,
                    vehicleTransportToDays: pg.vehicleTransportToDays,
                    vehicleTransportFromDays: pg.vehicleTransportFromDays,
                    outboundTransportSenderId:
                      pg.outboundTransportSender &&
                      pg.outboundTransportSender._id,
                    outboundTransportRecipientId:
                      pg.outboundTransportRecipient &&
                      pg.outboundTransportRecipient._id,
                    returnTransportSenderId:
                      pg.returnTransportSender && pg.returnTransportSender._id,
                    returnTransportRecipientId:
                      pg.returnTransportRecipient &&
                      pg.returnTransportRecipient._id,
                    noteVehicles: pg.noteVehicles,
                    noteMaterials: pg.noteMaterials,
                    note1: pg.note1,
                    note2: pg.note2,
                    note3: pg.note3,
                    annotation: pg.annotation,
                    clearingIds: pg.clearing,
                    editorsIds: pg.editors,
                    resourceManagersIds: pg.resourceManagers,
                    authorizedEditorsIds: pg.authorizedEditors,
                    block1: pg.block1,
                    block2: pg.block2,
                    block3: pg.block3,
                    block4: pg.block4,
                    block5: pg.block5,
                    block6: pg.block6,
                    block7: pg.block7,
                    block8: pg.block8,
                    block9: pg.block9,
                    block10: pg.block10,
                    block1Note: pg.block1Note,
                    block2Note: pg.block2Note,
                    block3Note: pg.block3Note,
                    block4Note: pg.block4Note,
                    block5Note: pg.block5Note,
                    block6Note: pg.block6Note,
                    block7Note: pg.block7Note,
                    block8Note: pg.block8Note,
                    block9Note: pg.block9Note,
                    block10Note: pg.block10Note,
                    block1Files: pg.block1Files,
                    block2Files: pg.block2Files,
                    block3Files: pg.block3Files,
                    block4Files: pg.block4Files,
                    block5Files: pg.block5Files,
                    block6Files: pg.block6Files,
                    block7Files: pg.block7Files,
                    block8Files: pg.block8Files,
                    block9Files: pg.block9Files,
                    block10Files: pg.block10Files,
                    fixedRates: omitDeep(pg.fixedRates, ['__typename']),
                  })
                )
              })()
            : false
        }
      />
      <FormikPanel
        {...panelState}
        validationSchema={provingGroundFormSchema}
        type={PanelType.extraLarge}
        isLightDismiss={true}
        onSubmit={handleSubmit}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('model.provingGround.edit')
            : t('model.provingGround.create')
        }
      >
        <ProvingGroundForm
          filesUploading={filesUploading}
          selectedItemId={selectedItemId}
        />
      </FormikPanel>

      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const AdminProvingGroundsView: React.FC = () => (
  <ViewAuthorizer
    roles={[UserRole.SystemAdministrator, UserRole.ResourceManager]}
  >
    <StickyPane>
      <AppCommandBar />
      <AdminProvingGroundsListView />
    </StickyPane>
  </ViewAuthorizer>
)
